import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  Sparkles, Users, Search, BarChart3, Settings, Shield, 
  Trash2, Edit, UserPlus, Ban, CheckCircle, XCircle,
  ChevronLeft, ChevronRight, RefreshCw, Download, Filter,
  FileText, Database, Activity, TrendingUp, Clock, Globe
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SuperAdminPage = () => {
  const { user, token, logout } = useAuth();
  const navigate = useNavigate();
  
  // State
  const [activeTab, setActiveTab] = useState('users');
  const [users, setUsers] = useState([]);
  const [searches, setSearches] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [editingUser, setEditingUser] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const itemsPerPage = 10;

  useEffect(() => {
    if (!user || (user.role !== 'admin' && user.role !== 'super_admin')) {
      navigate('/admin/login');
      return;
    }
    fetchData();
  }, [user, activeTab]);

  const fetchData = async () => {
    setLoading(true);
    try {
      if (activeTab === 'users') {
        const response = await axios.get(`${API}/admin/users`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setUsers(response.data.users || []);
      } else if (activeTab === 'searches') {
        const response = await axios.get(`${API}/admin/searches`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSearches(response.data.searches || []);
      } else if (activeTab === 'analytics') {
        const [searchRes, userRes] = await Promise.all([
          axios.get(`${API}/analytics/searches`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${API}/analytics/users`, { headers: { Authorization: `Bearer ${token}` } })
        ]);
        setAnalytics({ searches: searchRes.data, users: userRes.data });
      }
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateRole = async (userId, newRole) => {
    try {
      await axios.put(`${API}/admin/users/${userId}/role`, 
        { role: newRole },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('User role updated');
      fetchData();
      setEditDialogOpen(false);
    } catch (error) {
      toast.error('Failed to update role');
    }
  };

  const handleDeleteUser = async () => {
    if (!userToDelete) return;
    try {
      await axios.delete(`${API}/admin/users/${userToDelete.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('User deleted');
      fetchData();
      setDeleteDialogOpen(false);
      setUserToDelete(null);
    } catch (error) {
      toast.error('Failed to delete user');
    }
  };

  const handleToggleStatus = async (userId, currentStatus) => {
    try {
      await axios.put(`${API}/admin/users/${userId}/status`, 
        { status: currentStatus === 'active' ? 'suspended' : 'active' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('User status updated');
      fetchData();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleExportUsers = async () => {
    try {
      const response = await axios.get(`${API}/admin/users/export`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `users_export_${new Date().toISOString().split('T')[0]}.json`;
      a.click();
      toast.success('Users exported');
    } catch (error) {
      toast.error('Failed to export users');
    }
  };

  // Filter users
  const filteredUsers = users.filter(u => {
    const matchesSearch = u.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         u.full_name?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesRole = roleFilter === 'all' || u.role === roleFilter;
    return matchesSearch && matchesRole;
  });

  // Pagination
  const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);
  const paginatedUsers = filteredUsers.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const tabs = [
    { id: 'users', name: 'Users', icon: Users },
    { id: 'searches', name: 'Searches', icon: Search },
    { id: 'analytics', name: 'Analytics', icon: BarChart3 },
    { id: 'settings', name: 'Settings', icon: Settings }
  ];

  if (!user || (user.role !== 'admin' && user.role !== 'super_admin')) {
    return null;
  }

  return (
    <div className="min-h-screen bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-slate-900 via-background to-black">
      {/* Header */}
      <nav className="border-b border-border/50 backdrop-blur-md bg-card/30 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <Shield className="text-primary" size={28} />
              <div>
                <h1 className="text-xl sm:text-2xl font-heading font-bold text-foreground">
                  Super Admin
                </h1>
                <p className="text-xs text-muted-foreground">concurrenz.AI Management</p>
              </div>
            </div>
            <div className="flex items-center gap-2 w-full sm:w-auto">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => navigate('/search')}
                className="flex-1 sm:flex-none text-xs sm:text-sm"
              >
                <Globe size={16} className="mr-1 sm:mr-2" />
                <span className="hidden sm:inline">View</span> Site
              </Button>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={logout}
                className="text-xs sm:text-sm"
              >
                Logout
              </Button>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-8">
        {/* Tabs */}
        <div className="flex gap-1 sm:gap-2 mb-6 overflow-x-auto pb-2">
          {tabs.map(tab => (
            <Button
              key={tab.id}
              variant={activeTab === tab.id ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setActiveTab(tab.id)}
              className="flex-shrink-0 text-xs sm:text-sm"
              data-testid={`tab-${tab.id}`}
            >
              <tab.icon size={16} className="mr-1 sm:mr-2" />
              <span className="hidden sm:inline">{tab.name}</span>
              <span className="sm:hidden">{tab.name.slice(0, 4)}</span>
            </Button>
          ))}
        </div>

        {/* Users Tab */}
        {activeTab === 'users' && (
          <div className="space-y-4">
            {/* Filters */}
            <Card className="p-4 rounded-xl border bg-card/50 backdrop-blur-md">
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="flex-1">
                  <Input
                    placeholder="Search users..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full"
                    data-testid="search-users-input"
                  />
                </div>
                <div className="flex gap-2">
                  <Select value={roleFilter} onValueChange={setRoleFilter}>
                    <SelectTrigger className="w-full sm:w-32" data-testid="role-filter">
                      <SelectValue placeholder="Role" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Roles</SelectItem>
                      <SelectItem value="user">User</SelectItem>
                      <SelectItem value="admin">Admin</SelectItem>
                      <SelectItem value="super_admin">Super Admin</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button variant="outline" size="icon" onClick={fetchData}>
                    <RefreshCw size={16} />
                  </Button>
                  <Button variant="outline" size="icon" onClick={handleExportUsers}>
                    <Download size={16} />
                  </Button>
                </div>
              </div>
            </Card>

            {/* Users Table */}
            <Card className="rounded-xl border bg-card/50 backdrop-blur-md overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-background/50">
                    <tr>
                      <th className="text-left p-3 sm:p-4 text-xs sm:text-sm font-medium text-muted-foreground">User</th>
                      <th className="text-left p-3 sm:p-4 text-xs sm:text-sm font-medium text-muted-foreground hidden sm:table-cell">Role</th>
                      <th className="text-left p-3 sm:p-4 text-xs sm:text-sm font-medium text-muted-foreground hidden md:table-cell">Status</th>
                      <th className="text-left p-3 sm:p-4 text-xs sm:text-sm font-medium text-muted-foreground hidden lg:table-cell">Searches</th>
                      <th className="text-left p-3 sm:p-4 text-xs sm:text-sm font-medium text-muted-foreground hidden lg:table-cell">Joined</th>
                      <th className="text-right p-3 sm:p-4 text-xs sm:text-sm font-medium text-muted-foreground">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan={6} className="p-8 text-center text-muted-foreground">
                          <RefreshCw className="animate-spin mx-auto mb-2" size={24} />
                          Loading...
                        </td>
                      </tr>
                    ) : paginatedUsers.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="p-8 text-center text-muted-foreground">
                          No users found
                        </td>
                      </tr>
                    ) : paginatedUsers.map(u => (
                      <tr key={u.id} className="border-t border-border/50 hover:bg-background/30">
                        <td className="p-3 sm:p-4">
                          <div>
                            <p className="font-medium text-foreground text-sm">{u.full_name}</p>
                            <p className="text-xs text-muted-foreground">{u.email}</p>
                            <div className="sm:hidden mt-1">
                              <span className={`text-xs px-2 py-0.5 rounded-full ${
                                u.role === 'super_admin' ? 'bg-red-500/20 text-red-400' :
                                u.role === 'admin' ? 'bg-purple-500/20 text-purple-400' :
                                'bg-primary/20 text-primary'
                              }`}>
                                {u.role?.replace('_', ' ')}
                              </span>
                            </div>
                          </div>
                        </td>
                        <td className="p-3 sm:p-4 hidden sm:table-cell">
                          <span className={`text-xs px-2 py-1 rounded-full ${
                            u.role === 'super_admin' ? 'bg-red-500/20 text-red-400' :
                            u.role === 'admin' ? 'bg-purple-500/20 text-purple-400' :
                            'bg-primary/20 text-primary'
                          }`}>
                            {u.role?.replace('_', ' ')}
                          </span>
                        </td>
                        <td className="p-3 sm:p-4 hidden md:table-cell">
                          <span className={`text-xs px-2 py-1 rounded-full flex items-center gap-1 w-fit ${
                            u.status === 'suspended' ? 'bg-red-500/20 text-red-400' : 'bg-green-500/20 text-green-400'
                          }`}>
                            {u.status === 'suspended' ? <XCircle size={12} /> : <CheckCircle size={12} />}
                            {u.status || 'active'}
                          </span>
                        </td>
                        <td className="p-3 sm:p-4 text-sm text-muted-foreground hidden lg:table-cell">
                          {u.search_count || 0}
                        </td>
                        <td className="p-3 sm:p-4 text-xs text-muted-foreground hidden lg:table-cell">
                          {u.created_at ? new Date(u.created_at).toLocaleDateString() : 'N/A'}
                        </td>
                        <td className="p-3 sm:p-4">
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => { setEditingUser(u); setEditDialogOpen(true); }}
                              data-testid={`edit-user-${u.id}`}
                              className="h-8 w-8 p-0"
                            >
                              <Edit size={14} />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleToggleStatus(u.id, u.status)}
                              className="h-8 w-8 p-0"
                            >
                              {u.status === 'suspended' ? <CheckCircle size={14} className="text-green-400" /> : <Ban size={14} className="text-yellow-400" />}
                            </Button>
                            {user.role === 'super_admin' && u.role !== 'super_admin' && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => { setUserToDelete(u); setDeleteDialogOpen(true); }}
                                className="h-8 w-8 p-0 text-red-400 hover:text-red-300"
                              >
                                <Trash2 size={14} />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between p-4 border-t border-border/50">
                  <p className="text-xs sm:text-sm text-muted-foreground">
                    {filteredUsers.length} users
                  </p>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                    >
                      <ChevronLeft size={16} />
                    </Button>
                    <span className="text-sm">{currentPage} / {totalPages}</span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                    >
                      <ChevronRight size={16} />
                    </Button>
                  </div>
                </div>
              )}
            </Card>
          </div>
        )}

        {/* Searches Tab */}
        {activeTab === 'searches' && (
          <Card className="rounded-xl border bg-card/50 backdrop-blur-md overflow-hidden">
            <div className="p-4 border-b border-border/50">
              <h2 className="font-heading font-semibold text-foreground">Recent Searches</h2>
              <p className="text-xs text-muted-foreground">All search queries across the platform</p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-background/50">
                  <tr>
                    <th className="text-left p-3 sm:p-4 text-xs sm:text-sm font-medium text-muted-foreground">Query</th>
                    <th className="text-left p-3 sm:p-4 text-xs sm:text-sm font-medium text-muted-foreground hidden sm:table-cell">User</th>
                    <th className="text-left p-3 sm:p-4 text-xs sm:text-sm font-medium text-muted-foreground hidden md:table-cell">Model</th>
                    <th className="text-left p-3 sm:p-4 text-xs sm:text-sm font-medium text-muted-foreground">Time</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={4} className="p-8 text-center text-muted-foreground">
                        <RefreshCw className="animate-spin mx-auto mb-2" size={24} />
                        Loading...
                      </td>
                    </tr>
                  ) : searches.slice(0, 50).map((s, idx) => (
                    <tr key={idx} className="border-t border-border/50 hover:bg-background/30">
                      <td className="p-3 sm:p-4">
                        <p className="text-sm text-foreground truncate max-w-[150px] sm:max-w-xs">{s.query}</p>
                      </td>
                      <td className="p-3 sm:p-4 text-xs text-muted-foreground hidden sm:table-cell">
                        {s.user_email || 'Anonymous'}
                      </td>
                      <td className="p-3 sm:p-4 hidden md:table-cell">
                        <span className="text-xs px-2 py-1 rounded-full bg-primary/10 text-primary">
                          {s.model_used}
                        </span>
                      </td>
                      <td className="p-3 sm:p-4 text-xs text-muted-foreground">
                        {s.created_at ? new Date(s.created_at).toLocaleString() : 'N/A'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}

        {/* Analytics Tab */}
        {activeTab === 'analytics' && analytics && (
          <div className="space-y-6">
            {/* Stats Grid */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
              <Card className="p-4 sm:p-6 rounded-xl border bg-card/50 backdrop-blur-md">
                <div className="flex items-center gap-3 sm:gap-4">
                  <div className="p-2 sm:p-3 rounded-lg bg-primary/10">
                    <Search className="text-primary" size={20} />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Searches</p>
                    <p className="text-lg sm:text-2xl font-heading font-bold text-foreground">
                      {analytics.searches?.total_searches?.toLocaleString() || 0}
                    </p>
                  </div>
                </div>
              </Card>
              <Card className="p-4 sm:p-6 rounded-xl border bg-card/50 backdrop-blur-md">
                <div className="flex items-center gap-3 sm:gap-4">
                  <div className="p-2 sm:p-3 rounded-lg bg-green-500/10">
                    <Users className="text-green-500" size={20} />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Users</p>
                    <p className="text-lg sm:text-2xl font-heading font-bold text-foreground">
                      {analytics.users?.total_users?.toLocaleString() || 0}
                    </p>
                  </div>
                </div>
              </Card>
              <Card className="p-4 sm:p-6 rounded-xl border bg-card/50 backdrop-blur-md">
                <div className="flex items-center gap-3 sm:gap-4">
                  <div className="p-2 sm:p-3 rounded-lg bg-purple-500/10">
                    <Activity className="text-purple-500" size={20} />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Active</p>
                    <p className="text-lg sm:text-2xl font-heading font-bold text-foreground">
                      {analytics.users?.active_users?.toLocaleString() || 0}
                    </p>
                  </div>
                </div>
              </Card>
              <Card className="p-4 sm:p-6 rounded-xl border bg-card/50 backdrop-blur-md">
                <div className="flex items-center gap-3 sm:gap-4">
                  <div className="p-2 sm:p-3 rounded-lg bg-orange-500/10">
                    <TrendingUp className="text-orange-500" size={20} />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Images</p>
                    <p className="text-lg sm:text-2xl font-heading font-bold text-foreground">
                      {analytics.searches?.image_searches?.toLocaleString() || 0}
                    </p>
                  </div>
                </div>
              </Card>
            </div>

            {/* Charts */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
              <Card className="p-4 sm:p-6 rounded-xl border bg-card/50 backdrop-blur-md">
                <h3 className="font-heading font-semibold text-foreground mb-4 flex items-center gap-2">
                  <Clock size={18} className="text-primary" />
                  Daily Searches
                </h3>
                <div className="space-y-2">
                  {analytics.searches?.daily_searches?.map((day, idx) => (
                    <div key={idx} className="flex items-center gap-3">
                      <span className="text-xs text-muted-foreground w-20">{day.date}</span>
                      <div className="flex-1 bg-background/50 rounded-full h-4 overflow-hidden">
                        <div 
                          className="bg-primary h-full rounded-full"
                          style={{ 
                            width: `${Math.min(100, (day.count / Math.max(...analytics.searches.daily_searches.map(d => d.count), 1)) * 100)}%` 
                          }}
                        />
                      </div>
                      <span className="text-xs font-medium text-foreground w-8 text-right">{day.count}</span>
                    </div>
                  ))}
                </div>
              </Card>

              <Card className="p-4 sm:p-6 rounded-xl border bg-card/50 backdrop-blur-md">
                <h3 className="font-heading font-semibold text-foreground mb-4 flex items-center gap-2">
                  <TrendingUp size={18} className="text-primary" />
                  Top Queries
                </h3>
                <div className="space-y-2">
                  {analytics.searches?.top_queries?.slice(0, 5).map((q, idx) => (
                    <div key={idx} className="flex items-center justify-between p-2 rounded-lg bg-background/30">
                      <span className="text-sm text-foreground truncate flex-1 mr-2">{q.query}</span>
                      <span className="text-xs text-muted-foreground">{q.count}</span>
                    </div>
                  ))}
                </div>
              </Card>
            </div>
          </div>
        )}

        {/* Settings Tab */}
        {activeTab === 'settings' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
            <Card className="p-4 sm:p-6 rounded-xl border bg-card/50 backdrop-blur-md">
              <h3 className="font-heading font-semibold text-foreground mb-4 flex items-center gap-2">
                <Database size={18} className="text-primary" />
                System Info
              </h3>
              <div className="space-y-3">
                <div className="flex justify-between p-2 rounded-lg bg-background/30">
                  <span className="text-sm text-muted-foreground">Platform</span>
                  <span className="text-sm text-foreground">concurrenz.AI</span>
                </div>
                <div className="flex justify-between p-2 rounded-lg bg-background/30">
                  <span className="text-sm text-muted-foreground">Version</span>
                  <span className="text-sm text-foreground">2.0.0</span>
                </div>
                <div className="flex justify-between p-2 rounded-lg bg-background/30">
                  <span className="text-sm text-muted-foreground">Environment</span>
                  <span className="text-sm text-foreground">Production</span>
                </div>
              </div>
            </Card>

            <Card className="p-4 sm:p-6 rounded-xl border bg-card/50 backdrop-blur-md">
              <h3 className="font-heading font-semibold text-foreground mb-4 flex items-center gap-2">
                <Shield size={18} className="text-primary" />
                Quick Actions
              </h3>
              <div className="space-y-2">
                <Button variant="outline" className="w-full justify-start" onClick={fetchData}>
                  <RefreshCw size={16} className="mr-2" />
                  Refresh All Data
                </Button>
                <Button variant="outline" className="w-full justify-start" onClick={handleExportUsers}>
                  <Download size={16} className="mr-2" />
                  Export All Users
                </Button>
                <Button variant="outline" className="w-full justify-start" onClick={() => navigate('/dashboard')}>
                  <FileText size={16} className="mr-2" />
                  Content Management
                </Button>
              </div>
            </Card>
          </div>
        )}
      </div>

      {/* Edit User Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Edit User</DialogTitle>
            <DialogDescription>Update user role and permissions</DialogDescription>
          </DialogHeader>
          {editingUser && (
            <div className="space-y-4 mt-4">
              <div>
                <p className="text-sm font-medium text-foreground">{editingUser.full_name}</p>
                <p className="text-xs text-muted-foreground">{editingUser.email}</p>
              </div>
              <div>
                <label className="text-sm font-medium">Role</label>
                <Select 
                  defaultValue={editingUser.role} 
                  onValueChange={(v) => handleUpdateRole(editingUser.id, v)}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="user">User</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                    {user.role === 'super_admin' && (
                      <SelectItem value="super_admin">Super Admin</SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Delete User</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete {userToDelete?.full_name}? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-2 mt-4">
            <Button variant="outline" className="flex-1" onClick={() => setDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button variant="destructive" className="flex-1" onClick={handleDeleteUser}>
              Delete
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SuperAdminPage;
